// ============================================
// COMMUNITY SERVICE - Frontend API Calls
// ============================================

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface Disease {
  code: string;
  name: string;
  category: string;
  shortDescription: string;
  memberCount: number;
  postCount: number;
  isActive: boolean;
}

export interface Post {
  id: string;
  userId: string;
  diseaseCode?: string;
  type: 'QUESTION' | 'EXPERIENCE' | 'VICTORY' | 'TIP' | 'SUPPORT' | 'GENERAL';
  title?: string;
  content: string;
  imageUrls: string[];
  isAnonymous: boolean;
  likesCount: number;
  commentsCount: number;
  viewsCount: number;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    userType: string;
  };
  disease?: {
    code: string;
    name: string;
    category: string;
  };
  reactions?: Array<{ type: string }>;
  _count?: {
    comments: number;
    reactions: number;
  };
}

export interface Comment {
  id: string;
  postId: string;
  userId: string;
  content: string;
  imageUrl?: string;
  isAnonymous: boolean;
  likesCount: number;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    userType: string;
  };
  likes?: Array<{ id: string }>;
  _count?: {
    likes: number;
  };
}

export interface Conversation {
  conversationId: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    profilePhotoUrl?: string | null;
  };
  lastMessage: {
    content: string;
    createdAt: string;
    isSentByMe: boolean;
  } | null;
  unreadCount: number;
  lastMessageAt: string;
}

export interface ConnectionUser {
  id: string;
  firstName: string;
  lastName: string;
  userType: string;
  profilePhotoUrl?: string | null;
  isFriend?: boolean;
  iFollow?: boolean;
  followsMe?: boolean;
  friendSince?: string;
}

export interface ConnectionStatus {
  iFollow: boolean;
  theyFollowMe: boolean;
  areFriends: boolean;
  isSelf: boolean;
  blockedByMe: boolean;
  blockedByThem: boolean;
}

export interface BlockedUser {
  id: string;
  firstName: string;
  lastName: string;
  userType: string;
  profilePhotoUrl?: string | null;
  blockedAt: string;
}



export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  receiverId: string;
  content: string;
  imageUrl?: string;
  isRead: boolean;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export const communityService = {
  // ==================== DISEASES ====================
  async getDiseases(): Promise<Disease[]> {
    const response = await axios.get(`${API_URL}/api/community/diseases`, {
      headers: getAuthHeader()
    });
    return response.data.diseases;
  },

  async getDiseaseDetails(code: string) {
    const response = await axios.get(`${API_URL}/api/community/diseases/${code}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  async getDiseaseMembers(code: string, limit: number = 12): Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    userType: string;
    profilePhotoUrl?: string | null;
    memberSince: string;
  }>> {
    const response = await axios.get(`${API_URL}/api/community/diseases/${code}/members?limit=${limit}`, {
      headers: getAuthHeader()
    });
    return response.data.members;
  },

  // ==================== POSTS ====================
  async getPosts(filters?: {
    diseaseCode?: string;
    type?: string;
    userId?: string;
    page?: number;
    limit?: number;
    sortBy?: 'recent' | 'popular' | 'unanswered';
  }) {
    const params = new URLSearchParams();
    if (filters?.diseaseCode) params.append('diseaseCode', filters.diseaseCode);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);

    const response = await axios.get(`${API_URL}/api/community/posts?${params}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  async getPostById(postId: string) {
    const response = await axios.get(`${API_URL}/api/community/posts/${postId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  async createPost(data: {
    diseaseCode?: string;
    type: string;
    title?: string;
    content: string;
    imageUrls?: string[];
    isAnonymous?: boolean;
  }): Promise<Post> {
    const response = await axios.post(`${API_URL}/api/community/posts`, data, {
      headers: getAuthHeader()
    });
    return response.data.post;
  },

  async updatePost(postId: string, data: {
    title?: string;
    content?: string;
    imageUrls?: string[];
  }): Promise<Post> {
    const response = await axios.put(`${API_URL}/api/community/posts/${postId}`, data, {
      headers: getAuthHeader()
    });
    return response.data.post;
  },

  async deletePost(postId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/posts/${postId}`, {
      headers: getAuthHeader()
    });
  },

  async reactToPost(postId: string, type: 'LIKE' | 'HEART' | 'SUPPORT' | 'HELPFUL' | 'CELEBRATE'): Promise<void> {
    await axios.post(`${API_URL}/api/community/posts/${postId}/react`, { type }, {
      headers: getAuthHeader()
    });
  },

  async removeReaction(postId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/posts/${postId}/react`, {
      headers: getAuthHeader()
    });
  },

  async reportPost(postId: string, reason: string, details?: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/posts/${postId}/report`, { reason, details }, {
      headers: getAuthHeader()
    });
  },

  // ==================== COMMENTS ====================
  async createComment(postId: string, data: {
    content: string;
    imageUrl?: string;
    isAnonymous?: boolean;
  }): Promise<Comment> {
    const response = await axios.post(`${API_URL}/api/community/posts/${postId}/comments`, data, {
      headers: getAuthHeader()
    });
    return response.data.comment;
  },

  async updateComment(commentId: string, content: string): Promise<Comment> {
    const response = await axios.put(`${API_URL}/api/community/comments/${commentId}`, { content }, {
      headers: getAuthHeader()
    });
    return response.data.comment;
  },

  async deleteComment(commentId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/comments/${commentId}`, {
      headers: getAuthHeader()
    });
  },

  async likeComment(commentId: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/comments/${commentId}/like`, {}, {
      headers: getAuthHeader()
    });
  },

  async unlikeComment(commentId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/comments/${commentId}/like`, {
      headers: getAuthHeader()
    });
  },

  async reportComment(commentId: string, reason: string, details?: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/comments/${commentId}/report`, { reason, details }, {
      headers: getAuthHeader()
    });
  },

  // ==================== MESSAGES ====================
  async getConversations(): Promise<Conversation[]> {
    const response = await axios.get(`${API_URL}/api/community/conversations`, {
      headers: getAuthHeader()
    });
    return response.data.conversations;
  },

  async getMessages(userId: string, page: number = 1) {
    const response = await axios.get(`${API_URL}/api/community/conversations/${userId}?page=${page}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  async sendMessage(userId: string, content: string, imageUrl?: string): Promise<Message> {
    const response = await axios.post(`${API_URL}/api/community/conversations/${userId}/messages`, 
      { content, imageUrl }, 
      { headers: getAuthHeader() }
    );
    return response.data.message;
  },

  async markMessagesAsRead(userId: string): Promise<void> {
    await axios.put(`${API_URL}/api/community/conversations/${userId}/read`, {}, {
      headers: getAuthHeader()
    });
  },

  async getUnreadCount() {
    const response = await axios.get(`${API_URL}/api/community/messages/unread`, {
      headers: getAuthHeader()
    });
    return response.data.unreadCount;
  },

  async deleteMessage(messageId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/messages/${messageId}`, {
      headers: getAuthHeader()
    });
  },

  async getUserOnlineStatus(userId: string): Promise<{ isOnline: boolean; lastSeen: string | null }> {
    const response = await axios.get(`${API_URL}/api/community/users/${userId}/online-status`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // ==================== USER CONNECTIONS ====================
  async followUser(userId: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/users/${userId}/follow`, {}, {
      headers: getAuthHeader()
    });
  },

  async unfollowUser(userId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/users/${userId}/follow`, {
      headers: getAuthHeader()
    });
  },

  async getFollowers(userId: string): Promise<ConnectionUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/${userId}/followers`, {
      headers: getAuthHeader()
    });
    return response.data.followers;
  },

  async getFollowing(userId: string): Promise<ConnectionUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/${userId}/following`, {
      headers: getAuthHeader()
    });
    return response.data.following;
  },

  // Current-user-focused endpoints (returns enriched with isFriend / iFollow / followsMe)
  async getMyFriends(): Promise<ConnectionUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/me/friends`, {
      headers: getAuthHeader()
    });
    return response.data.friends;
  },

  async getMyFollowing(): Promise<ConnectionUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/me/following`, {
      headers: getAuthHeader()
    });
    return response.data.following;
  },

  async getMyFollowers(): Promise<ConnectionUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/me/followers`, {
      headers: getAuthHeader()
    });
    return response.data.followers;
  },

  async getConnectionStatus(userId: string): Promise<ConnectionStatus> {
    const response = await axios.get(`${API_URL}/api/community/users/${userId}/connection-status`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  async getUserProfile(userId: string) {

    const response = await axios.get(`${API_URL}/api/community/users/${userId}/profile`, {
      headers: getAuthHeader()
    });
    return response.data.profile;
  },

  // ==================== UNFRIEND & BLOCK ====================
  async unfriendUser(userId: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/users/${userId}/unfriend`, {}, {
      headers: getAuthHeader()
    });
  },

  async blockUser(userId: string): Promise<void> {
    await axios.post(`${API_URL}/api/community/users/${userId}/block`, {}, {
      headers: getAuthHeader()
    });
  },

  async unblockUser(userId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/community/users/${userId}/block`, {
      headers: getAuthHeader()
    });
  },

  async getBlockedUsers(): Promise<BlockedUser[]> {
    const response = await axios.get(`${API_URL}/api/community/users/me/blocked`, {
      headers: getAuthHeader()
    });
    return response.data.blocked;
  },

  // ==================== ADMIN/MODERATION ====================

  async getReports() {
    const response = await axios.get(`${API_URL}/api/community/reports`, {
      headers: getAuthHeader()
    });
    return response.data.reports;
  },

  async resolveReport(reportId: string, action: 'resolve' | 'dismiss') {
    const response = await axios.put(`${API_URL}/api/community/reports/${reportId}/resolve`, 
      { action }, 
      { headers: getAuthHeader() }
    );
    return response.data;
  }
};
